import DataTable from 'Components/DataTable';
import { Container, Item } from 'Components/Grid';
import HistoryRowSubHeader from 'Components/HistoryRowSubHeader';
import { isNumber, nowPartialDate, partialDateFromDate, partialDateToValue, sortPartialDate } from 'neuro-utils';
import { formatPartialDate } from 'Utility/date';
import * as React from 'react';
import { useAppSelector as useSelector } from 'Store/index';
import { MyServiceContext } from '..';
import ActionButtonRounded from 'Components/ActionButtonRounded';

const InvitesHistory = ({
  invites,
  inviteErrors,
  taskLists,
}: {
  invites: Array<Partial<TNeuroStimulus>>;
  inviteErrors: Array<{ method: 'sms' | 'email'; statusCode: number }>;
  taskLists: Array<ITaskList>;
}): React.JSX.Element => {
  const myServContext = React.useContext(MyServiceContext);
  const { fm, setEditingObj, platform } = myServContext;

  const orgs = useSelector((s: IState) => s.session.orgGroupArray);
  const orgTaskUISettings = useSelector((s: IState) => s.settings.orgSettings.taskUiSettings);
  const patientContactInfo = useSelector((s: { patient: IPatientStore }) => s.patient.contactInfo);

  const invitesData: Array<[React.JSX.Element, string, string, string]> = invites
    .toSorted((a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0))
    .filter(i => isNumber(i.timestamp))
    .map(i => [
      <div key={`date-${i.id}`} style={{ whiteSpace: 'nowrap', paddingRight: '0.5rem' }}>
        {formatPartialDate(partialDateFromDate(new Date(i.timestamp ?? 0)))}
      </div>,
      fm(`myService.opts.${i.medium === 'EMAIL' ? 'email' : 'sms'}`),
      i.carrier ? i.carrier : '',
      i.org_id ? orgs?.find(o => o.orgId === i.org_id)?.displayName || i.org_id : '',
    ]);

  const firstInviteUpcoming = invites.length === 0 && taskLists.length > 0;

  const firstTaskListSendDate = taskLists.toSorted((a, b) => sortPartialDate(a.sendDate, b.sendDate))[0]?.sendDate;

  const hasTaskUISettingsForPlatform = !!(platform && orgTaskUISettings?.[platform]);

  return (
    <>
      <Container style={{ justifyContent: 'space-between' }}>
        <Item>
          <HistoryRowSubHeader header={fm('myService.invites')} />
        </Item>

        <Item>
          <ActionButtonRounded
            text="myService.newInvite"
            filled={true}
            uppercase={false}
            width={9.6}
            height={3}
            fontSize={16}
            onClick={() => {
              setEditingObj({
                type: 'invite',
                data: null,
              });
            }}
            disabled={!hasTaskUISettingsForPlatform || firstInviteUpcoming || !!patientContactInfo?.contactNotAllowed}
            disabledTooltip={
              !hasTaskUISettingsForPlatform ? (
                <span>{fm('myService.noTasklistSettingsDefined')}</span>
              ) : firstInviteUpcoming ? undefined : (
                <span>{fm('myService.newInviteDisabledTooltip')}</span>
              )
            }
          />
        </Item>
      </Container>
      {firstInviteUpcoming ? (
        fm(
          `myService.firstInviteUpcomingInfo.${partialDateToValue(firstTaskListSendDate) < partialDateToValue(nowPartialDate()) ? 'past' : 'present'}`,
          {
            date: formatPartialDate(firstTaskListSendDate),
            strong: chunks => (
              <span key={`${chunks}`} style={{ fontWeight: '600' }}>
                {React.Children.toArray(chunks)}
              </span>
            ),
          },
        )
      ) : (
        <DataTable
          headers={['sendingDate', 'sendingMedium', 'numberOrAddress', 'sendingOrg']}
          headersFormatter={h => h && fm(`myService.${h}`)}
          data={{ rowData: invitesData }}
        />
      )}
      <div style={{ color: 'red', marginTop: '2rem' }}>
        {inviteErrors.map(
          (err, i) =>
            err.statusCode === 429 && (
              <div key={err.method + i}>
                {fm('errors.invites.' + err.method)}
                {' - '}
                {fm('errors.invites.' + err.statusCode)}
              </div>
            ),
        )}
      </div>
    </>
  );
};

export default InvitesHistory;
